import { useCallback, useContext, useMemo, useSyncExternalStore } from 'react';
import { RendererContext } from '../contexts/RendererContext';
import { ViewManagerSnapshot } from '../potree/personas/ViewManager';

const defaultSnapshot: ViewManagerSnapshot = {
  currentViewId: null,
  in2DMode: false,
  planeId: '',
  availableViews: [],
};

const noop = () => undefined;

interface UseViewManagerResult {
  in2DMode: boolean;
  planeId: string;
  availableViews: ViewManagerSnapshot['availableViews'];
  currentViewId: string | null;
  toggle2DMode: (planeId: string) => void;
  enable2DMode: (planeId: string) => void;
  disable2DMode: () => void;
  setView: (viewId: string) => void;
}

/**
 * React hook for view state from potree's ViewManager.
 *
 * Uses useSyncExternalStore to subscribe directly to ViewManager — no mirrored state.
 * Action methods delegate to ViewManager, which is the single source of truth.
 */
export function useViewManager(): UseViewManagerResult {
  const { viewer } = useContext(RendererContext);
  const viewManager = viewer?.personaManager?.viewManager;

  const subscribe = useCallback(
    (callback: () => void) => {
      if (!viewManager) return noop;
      return viewManager.subscribe(callback);
    },
    [viewManager],
  );

  const getSnapshot = useCallback(() => {
    if (!viewManager) return defaultSnapshot;
    return viewManager.getSnapshot();
  }, [viewManager]);

  const snapshot = useSyncExternalStore(subscribe, getSnapshot);

  const toggle2DMode = useCallback(
    (planeId: string) => viewManager?.toggle2DViewMode(planeId),
    [viewManager],
  );

  const enable2DMode = useCallback(
    (planeId: string) => viewManager?.enable2DViewMode(planeId),
    [viewManager],
  );

  const disable2DMode = useCallback(
    () => viewManager?.disable2DViewMode(),
    [viewManager],
  );

  const setView = useCallback(
    (viewId: string) => viewManager?.setViewById(viewId),
    [viewManager],
  );

  return useMemo(
    () => ({
      ...snapshot,
      toggle2DMode,
      enable2DMode,
      disable2DMode,
      setView,
    }),
    [snapshot, toggle2DMode, enable2DMode, disable2DMode, setView],
  );
}
